import { useSyncExternalStore } from 'react';

/**
 * Admin Job Monitor store — an in-memory log of every job started this page
 * load (generate + retry/clone), surfaced by the hidden <JobMonitor> debug
 * panel (?apb_debug=1). NOT the customer-facing "Recent Portraits".
 *
 * Module-level (not component state) so it survives across the two hook
 * instances on a page (App + PreviewApp) and any re-mounts. No persistence —
 * resets on reload, by design.
 */
export interface JobRecord {
  job_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  portrait_mode: string;
  style_id: string;
  style_name: string;
  product_id: string;
  started_at: string;        // ISO timestamp
  completed_at?: string;     // ISO timestamp when done
  preview_url?: string;      // set when completed
  error?: string;            // set when failed
}

let jobs: JobRecord[] = [];
const listeners = new Set<() => void>();

function notify(): void {
  for (const l of listeners) l();
}

/** Add a new job (newest first). De-duped by job_id. */
export function recordJob(rec: JobRecord): void {
  if (jobs.some((j) => j.job_id === rec.job_id)) return;
  jobs = [rec, ...jobs];
  notify();
}

/** Merge a patch into the matching job_id record. No-op if not found. */
export function updateJob(jobId: string, patch: Partial<JobRecord>): void {
  let changed = false;
  jobs = jobs.map((j) => {
    if (j.job_id !== jobId) return j;
    changed = true;
    return { ...j, ...patch };
  });
  if (changed) notify();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

function getSnapshot(): JobRecord[] {
  return jobs;
}

/** Reactive view of the job history for the monitor panel. */
export function useJobHistory(): JobRecord[] {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
